import { useEffect } from "react";
import { useRestaurant } from "@/hooks/use-restaurant";
import { useTenantBranding } from "@/hooks/use-tenant-branding";

/** Static first-paint fallback from `public/index.html`. */
export const PLATFORM_BROWSER_CHROME_COLOR = "#FFFFFF";

const THEME_COLOR_SELECTOR = 'meta[name="theme-color"]';

function queryThemeColorMetas(): HTMLMetaElement[] {
  return Array.from(document.head.querySelectorAll(THEME_COLOR_SELECTOR)).filter(
    (el): el is HTMLMetaElement => el instanceof HTMLMetaElement
  );
}

function ensureThemeColorMeta(): HTMLMetaElement {
  const existing = queryThemeColorMetas();
  const meta = existing[0] ?? document.createElement("meta");
  for (const extra of existing.slice(1)) {
    extra.remove();
  }
  meta.setAttribute("name", "theme-color");
  if (!meta.isConnected) {
    document.head.appendChild(meta);
  }
  return meta;
}

function applyThemeColor(content: string): void {
  const meta = ensureThemeColorMeta();
  if (meta.getAttribute("content") !== content) {
    meta.setAttribute("content", content);
  }
}

/**
 * Global browser-chrome owner for `meta[name="theme-color"]`.
 * Ready + admitted custom Theme Color → that color.
 * Not ready / fail-closed / unmount → platform surface `#FFFFFF`.
 * Updates the existing static tag; never leaves a duplicate.
 */
export function useTenantBrowserChrome(): void {
  const { isReady, restaurantId } = useRestaurant();
  const { themeColor, isCustomTheme } = useTenantBranding();

  useEffect(() => {
    if (typeof document === "undefined") return;

    const content =
      isReady && isCustomTheme ? themeColor : PLATFORM_BROWSER_CHROME_COLOR;
    applyThemeColor(content);

    return () => {
      applyThemeColor(PLATFORM_BROWSER_CHROME_COLOR);
    };
  }, [isReady, isCustomTheme, restaurantId, themeColor]);
}

export function TenantBrowserChrome(): null {
  useTenantBrowserChrome();
  return null;
}
